import { useLocalStorageState } from 'ahooks';
import { Form, FormInstance } from 'antd';
import { isEmpty, isPlainObject } from 'lodash-es';
import React, { FC, useEffect, useRef, useState } from 'react';
import { getProductId } from '@/infrastructure/product-info';
import { getCompareDateValue, isDataLabelingPlatform, searchValueDeserializer } from '@/infrastructure/utils/issue';
import { toArray } from '@/infrastructure/utils/to-array';
import {
  FormEntry,
  FormType, IFormRef,
  QueryForm,
} from '@/presentation/components/form';
import { NumberOperator } from '@/presentation/components/form/components/select-number';
import { DEFAULT_MONITOR_LIMIT_DAYS } from '@/presentation/components/form/components/time-range';
import { VersionSelectGroupName } from '@/presentation/components/form/components/version-select-group';
import { INFERRED_LABELS_OPTIONS, MatchMode } from '@/presentation/components/form/standards/types';
import { FilterEntry, FormColumnGroup } from '@/presentation/components/form/types';
import {
  getShowSwitchStatus,
  getVersionLabel,
  onChangeAppVersion,
  TOP_RULES,
  topNumLabel,
} from '@/presentation/components/form/utils';
import { BUNDLE_TIPS_CASE, hintText } from '@/presentation/components/hint-text/hintText';
import { DATA_LABELING_TIPS } from '@/presentation/components/issue-list/components/tips';
import { SEARCH_VALUES } from '@/presentation/config/storage';
import { SearchValueStorageType } from '@/presentation/config/type';
import { getIssueFilterConfig } from '@/presentation/pages/setting/data-labeling/config';
import { useStores } from '@/presentation/store';
import {
  AppVersionOperator,
  FIELD_ALL,
  FIELD_LABEL,
  FieldKey,
  Zone,
} from '@/proto/field';
import { IssueType } from '@/proto/issue';
import { FieldList } from '@/proto/token';

interface IProps {
  onFinish: (value: Record<string, any>) => void;
  entry: FilterEntry;
  initialValue?: FieldList & { timeRange: string[], isValueInit?: boolean };
  onValueInited?: (value: Record<string, any>) => void;
  form: FormInstance;
  tokenInitValues: FieldList & { isValueInit?: boolean };
}

export const ProblemForm: FC<IProps> = (props) => {
  const { tokenInitValues } = props;
  const [topForm] = Form.useForm();
  const tableRef = useRef<IFormRef>(null);
  const form = props.form ? props.form : topForm;
  const [showSwitch, setShowSwitch] = useState(false);

  const [searchValues, setSearchValues] = useLocalStorageState<SearchValueStorageType>(`${SEARCH_VALUES}-${props.entry}-${getProductId()}`, {
    defaultValue: [],
  });
  const { filterConfig } = useStores().ProductStore;
  const inferredLabelsConfig = getIssueFilterConfig(IssueType.ISSUE_TYPE_FOOM, filterConfig);

  useEffect(() => {
    if (!isEmpty(tokenInitValues)) {
      const { isValueInit = true, appVersionGroup, appVersionOperator } = tokenInitValues;
      // 链接有token时，强制展示有value值的表单项
      tableRef?.current?.showFormItem?.(entries, tokenInitValues)?.then(() => {
        form.setFieldsValue(tokenInitValues);
        if (isValueInit) {
          props.onValueInited?.(form.getFieldsValue());
        }
        // 初始化显示 【只显示新增问题】字段
        setShowSwitch(getShowSwitchStatus({
          values: toArray(appVersionGroup ?? ''),
          operator: appVersionOperator ?? '',
        }));
      });
    }
  }, [tokenInitValues]);

  useEffect(() => {
    if (!isEmpty(props.initialValue)) {
      const { isValueInit = true, appVersionGroup, appVersionOperator } = props.initialValue;
      form.setFieldsValue(props.initialValue);
      if (isValueInit) {
        props.onValueInited?.(form.getFieldsValue());
      }
      // 初始化显示 【只显示新增问题】字段
      setShowSwitch(getShowSwitchStatus({
        values: toArray(appVersionGroup ?? ''),
        operator: appVersionOperator ?? '',
      }));
    }
  }, [props.initialValue]);

  const entries: FormEntry[] = [
    {
      type: FormType.TimeRangeCascade,
      name: 'timeRange',
      labelString: FIELD_LABEL[FieldKey.TimeRange],
      label: FIELD_LABEL[FieldKey.TimeRange],
      scope: 'issues',
      deleteable: false,
      limitDays: DEFAULT_MONITOR_LIMIT_DAYS,
      showTime: props.entry === FilterEntry.FoomMallocList || props.entry === FilterEntry.FoomMallocDetail,
      hiddenTimeWindow: true,
    },
    {
      type: FormType.VersionSelectGroup,
      leftName: FieldKey.AppVersionOperator,
      rightName: FieldKey.AppVersion,
      label: FIELD_LABEL[FieldKey.AppVersionOperator],
      leftFieldKey: FieldKey.AppVersionOperator,
      rightFieldKey: FieldKey.AppVersion,
      fieldScope: Zone.FoomMalloc,
      scope: 'issues',
      mode: 'tags',
      leftInitialValue: AppVersionOperator.Or,
      rightInitialValue: [FIELD_ALL],
      name: VersionSelectGroupName.appVersionGroup,
      deleteable: false,
      hasMaxWidth: true,
    },
    {
      type: FormType.Select,
      name: 'appVersionMode',
      label: FIELD_LABEL[FieldKey.AppVersionMode],
      fieldKey: FieldKey.AppVersionMode,
      fieldScope: Zone.FoomMalloc,
      mode: 'multiple',
      initialValue: [FIELD_ALL],
      group: FormColumnGroup.ApplicationInfo,
    },
    {
      type: FormType.Select,
      name: 'scene',
      label: FIELD_LABEL[FieldKey.Scene],
      fieldKey: FieldKey.Scene,
      fieldScope: Zone.FoomMalloc,
      mode: 'single',
      initialValue: FIELD_ALL,
      dropdownMatchSelectWidth: 500,
      group: FormColumnGroup.ContextInfo,
    },
    {
      type: FormType.VersionSelect,
      name: FieldKey.OsVersion,
      versionName: FieldKey.OsVersion,
      label: getVersionLabel(FIELD_LABEL[FieldKey.OsVersion]),
      fieldKey: FieldKey.OsVersion,
      labelString: FIELD_LABEL[FieldKey.OsVersion],
      fieldScope: Zone.FoomMalloc,
      group: FormColumnGroup.DeviceInfo,
    },
    {
      type: FormType.Select,
      name: 'sdkVersion',
      label: FIELD_LABEL[FieldKey.SdkVersion],
      fieldKey: FieldKey.SdkVersion,
      fieldScope: Zone.FoomMalloc,
      mode: 'single',
      initialValue: FIELD_ALL,
      dropdownMatchSelectWidth: 300,
      group: FormColumnGroup.ApplicationInfo,
    },
    {
      type: FormType.Select,
      name: 'model',
      label: FIELD_LABEL[FieldKey.Model],
      fieldKey: FieldKey.Model,
      fieldScope: Zone.FoomMalloc,
      mode: 'tags',
      initialValue: FIELD_ALL,
      group: FormColumnGroup.DeviceInfo,
    },
    {
      type: FormType.Select,
      name: 'brand',
      label: FIELD_LABEL[FieldKey.Brand],
      fieldKey: FieldKey.Brand,
      fieldScope: Zone.FoomMalloc,
      mode: 'tags',
      initialValue: FIELD_ALL,
      dropdownMatchSelectWidth: 500,
      group: FormColumnGroup.DeviceInfo,
    },
    {
      type: FormType.Input,
      name: 'accountId',
      label: '用户ID',
      group: FormColumnGroup.UserInfo,
    },
    {
      type: FormType.Input,
      name: 'qimei',
      label: '设备ID',
      group: FormColumnGroup.UserInfo,
    },
    {
      type: FormType.Input,
      name: 'launchId',
      label: FIELD_LABEL[FieldKey.LaunchId],
      labelString: '启动ID',
      group: FormColumnGroup.ContextInfo,
    },
    {
      type: FormType.Input,
      name: 'clientIdentify',
      label: '消息ID',
      group: FormColumnGroup.exceptionInfo,
    },
    {
      type: FormType.Input,
      name: 'feature',
      label: 'Issue ID',
      disabled: props.entry !== FilterEntry.FoomMallocList,
      group: FormColumnGroup.exceptionInfo,
    },
    {
      type: FormType.SelectNumberGroup,
      name: FieldKey.AvgMallocSize,
      label: '分配大小均值（MB）',
      group: FormColumnGroup.ContextInfo,
      options: [{ value: NumberOperator.RANGE, label: '范围' }],
      processValue: (_result, allValues) => ({
        [FieldKey.AvgMallocSize]: allValues?.[FieldKey.AvgMallocSize]?.value
        || allValues?.[FieldKey.AvgMallocSize],
        [FieldKey.AvgMallocSizeCompare]: allValues?.[FieldKey.AvgMallocSizeCompare]?.value
        || allValues?.[FieldKey.AvgMallocSizeCompare],
      }),
      convertValue: (value) => {
        if (isPlainObject(value)) return value;
        return {
          operator: NumberOperator.RANGE,
          value,
        };
      },
      disabled: props.entry !== FilterEntry.FoomMallocList,
    },
    {
      type: FormType.SelectNumberGroup,
      name: FieldKey.MallocSize,
      label: '分配堆栈范围（MB）',
      group: FormColumnGroup.ContextInfo,
      options: [{ value: NumberOperator.RANGE, label: '范围' }],
      disabled: ![FilterEntry.FoomMallocList, FilterEntry.FoomMallocDetail].includes(props.entry),
      processValue: (_result, allValues) => ({
        [FieldKey.MallocSize]: allValues[FieldKey.MallocSize]?.value
        || allValues[FieldKey.MallocSize],
        [FieldKey.MallocSizeCompare]: allValues?.[FieldKey.AvgMallocSizeCompare]?.value
        || allValues?.[FieldKey.AvgMallocSizeCompare],
      }),
      convertValue: (value) => {
        if (isPlainObject(value)) return value;
        return {
          operator: NumberOperator.RANGE,
          value,
        };
      },
    },
    {
      type: FormType.SelectNumberGroup,
      name: FieldKey.UsedMem,
      label: 'FOOM前内存（MB）',
      group: FormColumnGroup.ContextInfo,
      options: [{ value: NumberOperator.RANGE, label: '范围' }],
      disabled: ![FilterEntry.FoomMallocList, FilterEntry.FoomMallocDetail].includes(props.entry),
      processValue: (_result, allValues) => ({
        [FieldKey.UsedMem]: allValues[FieldKey.UsedMem]?.value
        || allValues[FieldKey.UsedMem],
        [FieldKey.UsedMemCompare]: allValues?.[FieldKey.UsedMemCompare]?.value
        || allValues?.[FieldKey.UsedMemCompare],
      }),
      convertValue: (value) => {
        if (isPlainObject(value)) return value;
        return {
          operator: NumberOperator.RANGE,
          value,
        };
      },
    },
    {
      type: FormType.SelectNumberGroup,
      name: FieldKey.QimeiCount,
      label: '影响设备数',
      group: FormColumnGroup.exceptionInfo,
      options: [{ value: NumberOperator.RANGE, label: '范围' }],
      disabled: props.entry !== FilterEntry.FoomMallocList,
      processValue: (_result, allValues) => ({
        [FieldKey.QimeiCount]: allValues[FieldKey.QimeiCount]?.value
        || allValues[FieldKey.QimeiCount],
        [FieldKey.QimeiCountCompare]: allValues?.[FieldKey.QimeiCountCompare]?.value
        || allValues?.[FieldKey.QimeiCountCompare],
      }),
      convertValue: (value) => {
        if (isPlainObject(value)) return value;
        return {
          operator: NumberOperator.RANGE,
          value,
        };
      },
    },
    {
      type: FormType.SelectNumberGroup,
      name: FieldKey.DocCount,
      label: '影响次数',
      group: FormColumnGroup.exceptionInfo,
      options: [{ value: NumberOperator.RANGE, label: '范围' }],
      disabled: props.entry !== FilterEntry.FoomMallocList,
      processValue: (_result, allValues) => ({
        [FieldKey.DocCount]: allValues[FieldKey.DocCount]?.value
        || allValues[FieldKey.DocCount],
        [FieldKey.DocCountCompare]: allValues[FieldKey.DocCountCompare]?.value
        || allValues[FieldKey.DocCountCompare],
      }),
      convertValue: (value) => {
        if (isPlainObject(value)) return value;
        return {
          operator: NumberOperator.RANGE,
          value,
        };
      },
    },
    {
      type: FormType.SelectNumberGroup,
      name: FieldKey.P90MallocSize,
      label: '分配内存P90（MB）',
      group: FormColumnGroup.ContextInfo,
      options: [{ value: NumberOperator.RANGE, label: '范围' }],
      disabled: props.entry !== FilterEntry.FoomMallocList,
      processValue: (_result, allValues) => ({
        [FieldKey.P90MallocSize]: allValues[FieldKey.P90MallocSize]?.value
        || allValues[FieldKey.P90MallocSize],
        [FieldKey.P90MallocSizeCompare]: allValues[FieldKey.P90MallocSizeCompare]?.value
        || allValues[FieldKey.P90MallocSizeCompare],
      }),
      convertValue: (value) => {
        if (isPlainObject(value)) return value;
        return {
          operator: NumberOperator.RANGE,
          value,
        };
      },
    },
    {
      type: FormType.SelectNumberGroup,
      name: FieldKey.P50MallocSize,
      label: '分配内存P50（MB）',
      group: FormColumnGroup.ContextInfo,
      options: [{ value: NumberOperator.RANGE, label: '范围' }],
      disabled: props.entry !== FilterEntry.FoomMallocList,
      processValue: (_result, allValues) => ({
        [FieldKey.P50MallocSize]: allValues[FieldKey.P50MallocSize]?.value
        || allValues[FieldKey.P50MallocSize],
        [FieldKey.P50MallocSizeCompare]: allValues[FieldKey.P50MallocSizeCompare]?.value
        || allValues[FieldKey.P50MallocSizeCompare],
      }),
      convertValue: (value) => {
        if (isPlainObject(value)) return value;
        return {
          operator: NumberOperator.RANGE,
          value,
        };
      },
    },
    {
      type: FormType.Input,
      name: 'funcName',
      label: '问题特征',
      group: FormColumnGroup.exceptionInfo,
      disabled: props.entry !== FilterEntry.FoomMallocList,
    },
    {
      type: FormType.Select,
      name: 'bundleId',
      label: hintText(FIELD_LABEL[FieldKey.BundleId], BUNDLE_TIPS_CASE),
      labelString: FIELD_LABEL[FieldKey.BundleId],
      fieldKey: FieldKey.BundleId,
      fieldScope: Zone.FoomMalloc,
      mode: 'single',
      initialValue: FIELD_ALL,
      group: FormColumnGroup.DeviceInfo,
    },
    {
      type: FormType.Input,
      name: 'topNum',
      label: topNumLabel,
      rules: TOP_RULES,
      disabled: props.entry !== FilterEntry.FoomMallocList,
      labelString: 'Top问题数',
      group: FormColumnGroup.exceptionInfo,
    },
    {
      type: FormType.Select,
      name: 'showNew',
      label: '只显示新增问题',
      disabled: !showSwitch || props.entry !== FilterEntry.FoomMallocList,
      deleteable: false,
      mode: 'single',
      fieldKey: FieldKey.ShowNew,
      fieldScope: Zone.FoomMalloc,
      initialValue: false,
      compareDisabled: true,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardInferredLabels,
      label: hintText('数据标记', DATA_LABELING_TIPS),
      labelString: '数据标记',
      group: FormColumnGroup.Tag,
      mode: 'tags',
      options: inferredLabelsConfig?.labelOptions || [],
      initialValue: {
        mode: MatchMode.NOT,
        values: inferredLabelsConfig?.values?.map(item => item.value),
      },
      modeSelectProps: {
        defaultValue: MatchMode.NOT,
      },
      modeOptions: INFERRED_LABELS_OPTIONS,
      compareDisabled: true,
      disabled: !isDataLabelingPlatform(),
    },
    {
      type: FormType.TdsArea,
      name: FieldKey.StandardArea,
      label: '地域',
      group: FormColumnGroup.UserInfo,
      labelString: '地域',
      fieldScope: Zone.JavaLeak,
      compareDisabled: true,
      initialDeleted: true,
    },
  ];

  const onFinish = (value: Record<string, any>, compareSwitch?: boolean) => {
    const compareDateValue = getCompareDateValue(value, compareSwitch);
    if (compareSwitch) {
      form.setFieldsValue(compareDateValue);
    }
    props.onFinish({ ...value, ...compareDateValue });
  };

  return <QueryForm
    form={form}
    ref={tableRef}
    cachePrefix={'prefix1'}
    entries={entries}
    onFinish={onFinish}
    onChange={async (__, fields) => onChangeAppVersion({
      values: toArray(fields.appVersionGroup ?? ''),
      operator: fields?.appVersionOperator ?? '',
    }, setShowSwitch)}
    onReset={() => setShowSwitch(false)}
    pageEntry={props.entry}
    hasCompare={props.entry === FilterEntry.FoomMallocList}
    defaultValues={props.initialValue}
    issueType={IssueType.ISSUE_TYPE_FOOM_MALLOC}
    isSaveSearchValues={true}
    searchValues={searchValueDeserializer(searchValues)}
    setSearchValues={setSearchValues}
    renderByGroup
  />;
};
